import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import api, { formatDate } from "../lib/api";
import { mapCrmActivity } from "../lib/crmAdmin";
import { toast } from "sonner";
import { 
  Activity, 
  Phone, 
  Mail, 
  MessageSquare, 
  Calendar,
  FileText,
  Plus,
  Clock,
  User,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const activityTypes = [
  { value: "call", label: "Phone Call", icon: Phone, color: "text-blue-400", bg: "bg-blue-500/20" },
  { value: "email", label: "Email", icon: Mail, color: "text-emerald-400", bg: "bg-emerald-500/20" },
  { value: "meeting", label: "Meeting", icon: Calendar, color: "text-purple-400", bg: "bg-purple-500/20" },
  { value: "note", label: "Note", icon: FileText, color: "text-orange-400", bg: "bg-orange-500/20" },
  { value: "message", label: "Message", icon: MessageSquare, color: "text-pink-400", bg: "bg-pink-500/20" },
];

/* STARSEED-CRM-012: `crmPartyId` routes the timeline through the platform
   CRM (`/crm/admin/parties/{id}/activities` proxy → crm.activity.recorded
   events) instead of the legacy Mongo activities system. Callers pass ONE
   of userId / leadId / crmPartyId. */
const ActivityTimeline = ({ userId, leadId, crmPartyId, compact = false }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  const [submitting, setSubmitting] = useState(false);
  const [newActivity, setNewActivity] = useState({
    activity_type: "note",
    subject: "",
    description: "",
    contact_method: "",
    outcome: "",
  });

  const fetchActivities = useCallback(async () => {
    try {
      if (crmPartyId) {
        const response = await api.get(`/crm/admin/parties/${crmPartyId}/activities`);
        setActivities(
          (response.data.activities || [])
            .map(mapCrmActivity)
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        );
      } else {
        const endpoint = leadId
          ? `/activities/lead/${leadId}`
          : `/activities/user/${userId}`;
        const response = await api.get(endpoint);
        setActivities(response.data);
      }
    } catch (error) {
      // If endpoint doesn't exist yet, show empty state
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [userId, leadId, crmPartyId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newActivity.subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }

    setSubmitting(true);
    try {
      if (crmPartyId) {
        // Platform path: one crm.activity.recorded event; detail rides payload.
        await api.post(`/crm/admin/parties/${crmPartyId}/activities`, {
          activity_type: newActivity.activity_type,
          payload: {
            subject: newActivity.subject,
            description: newActivity.description,
            contact_method: newActivity.contact_method,
            outcome: newActivity.outcome,
          },
        });
      } else {
        await api.post("/activities", {
          ...newActivity,
          user_id: userId,
          lead_id: leadId,
        });
      }
      toast.success("Activity logged successfully");
      setShowAddDialog(false);
      setNewActivity({
        activity_type: "note",
        subject: "",
        description: "",
        contact_method: "",
        outcome: "",
      });
      fetchActivities();
    } catch (error) {
      toast.error("Failed to log activity");
    } finally {
      setSubmitting(false);
    }
  };

  const getActivityIcon = (type) => {
    const activity = activityTypes.find(a => a.value === type);
    return activity || activityTypes[3]; // Default to note
  };

  const displayedActivities = compact && !expanded 
    ? activities.slice(0, 3) 
    : activities;

  return (
    <Card className="bg-surface border-platinum/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-burgundy/20">
              <Activity className="text-burgundy" size={20} />
            </div>
            <div>
              <CardTitle className="text-cream font-serif text-lg">Activity Timeline</CardTitle>
              <CardDescription className="text-platinum-dark">Track calls, emails, and interactions</CardDescription>
            </div>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-testid="log-activity-btn">
                <Plus size={14} className="mr-1" />
                Log Activity
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-surface border-platinum/10 max-w-md" aria-describedby="add-activity-description">
              <DialogHeader>
                <DialogTitle className="text-cream font-serif">Log New Activity</DialogTitle>
                <p id="add-activity-description" className="text-platinum-dark text-sm">Record a call, email, meeting, or note</p>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-platinum">Activity Type</Label>
                  <Select 
                    value={newActivity.activity_type} 
                    onValueChange={(v) => setNewActivity(prev => ({ ...prev, activity_type: v }))}
                  >
                    <SelectTrigger className="bg-surface border-platinum/20 text-platinum">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-platinum/10">
                      {activityTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon size={14} className={type.color} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-platinum">Subject *</Label>
                  <Input
                    value={newActivity.subject}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Brief summary of the activity"
                    className="bg-white border-slate-200 text-slate-900"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-platinum">Description</Label>
                  <Textarea
                    value={newActivity.description}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Add details about the interaction..."
                    className="bg-white border-slate-200 text-slate-900 min-h-[100px]"
                  />
                </div>

                {(newActivity.activity_type === "call" || newActivity.activity_type === "email") && (
                  <div className="space-y-2">
                    <Label className="text-platinum">Outcome</Label>
                    <Select 
                      value={newActivity.outcome} 
                      onValueChange={(v) => setNewActivity(prev => ({ ...prev, outcome: v }))}
                    >
                      <SelectTrigger className="bg-surface border-platinum/20 text-platinum">
                        <SelectValue placeholder="Select outcome" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-platinum/10">
                        <SelectItem value="connected">Connected / Replied</SelectItem>
                        <SelectItem value="no_answer">No Answer / No Reply</SelectItem>
                        <SelectItem value="voicemail">Left Voicemail</SelectItem>
                        <SelectItem value="follow_up">Requires Follow-up</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-gold hover:bg-gold/90 text-navy-dark"
                  disabled={submitting}
                >
                  {submitting ? "Logging..." : "Log Activity"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-platinum/30 border-t-platinum rounded-full animate-spin"></div>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="text-platinum-dark mx-auto mb-3" size={32} />
            <p className="text-platinum-dark text-sm">No activities logged yet</p>
            <p className="text-platinum-dark text-xs mt-1">Click "Log Activity" to add your first entry</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {displayedActivities.map((activity, index) => {
                const typeInfo = getActivityIcon(activity.activity_type);
                const IconComponent = typeInfo.icon;
                return (
                  <div key={activity.id || index} className="relative pl-8">
                    {/* Timeline line */}
                    {index < displayedActivities.length - 1 && (
                      <div className="absolute left-3 top-8 bottom-0 w-px bg-platinum/10"></div>
                    )}
                    
                    {/* Icon */}
                    <div className={`absolute left-0 w-6 h-6 rounded-full ${typeInfo.bg} flex items-center justify-center`}>
                      <IconComponent size={12} className={typeInfo.color} />
                    </div>
                    
                    {/* Content. CAPMARKETS-149, review round 3 of PR #2404: this component renders
                        only in the Lead Detail dialog's Activity tab — portal content, outside
                        `.dashboard-shell`, where no light-theme remap reaches it — so every ink in
                        this file flips with the theme, and this card is `bg-surface-highlight`
                        rather than a navy tint (text-white on it measured 1.89:1 in light). */}
                    <div className="bg-surface-highlight rounded-lg p-3 border border-platinum/10">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-cream font-medium text-sm">{activity.subject}</p>
                          {activity.description && (
                            <p className="text-platinum-dark text-xs mt-1 line-clamp-2">{activity.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {activity.outcome && (
                            <Badge className="bg-platinum/10 text-cream text-xs">
                              {activity.outcome.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-platinum-dark">
                        <span className="flex items-center gap-1">
                          <Clock size={10} />
                          {formatDate(activity.created_at)}
                        </span>
                        {activity.created_by_name && (
                          <span className="flex items-center gap-1">
                            <User size={10} />
                            {activity.created_by_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Expand/Collapse for compact mode */}
            {compact && activities.length > 3 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="w-full mt-4 text-platinum-dark hover:text-cream"
              >
                {expanded ? (
                  <>
                    <ChevronUp size={14} className="mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} className="mr-1" />
                    Show All ({activities.length})
                  </>
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityTimeline;
